import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { DateTime } from 'luxon'

import {
  Accordion,
  Badge,
  Button,
  Card,
  CardColors,
  Icon,
  Link,
  Popup,
  Progress,
  Text,
} from '@/ui'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { useReselector } from '@/utils/sharedHooks'
import {
  formatCurrency,
  formatCurrencyFromCents,
} from '@/utils/currencyHelpers'
import { light, regular } from '@fortawesome/fontawesome-svg-core/import.macro'
import { useAnalyticsTrack } from '@/featuresV1/Amplitude'
import InsightsTrendGraph from '@/componentsV1/Finances/PracticeInsights/InsightsTrendGraph'
import { fetchInsightsBarchartData } from '@/componentsV1/Finances/shared/financeActions'
import {
  INSIGHTS_TYPE,
  processDataForChart,
} from '@/componentsV1/Finances/shared/utils'
import { useAppDispatch } from '@/utils/typeHelpers'
import { getFinancialProfile } from '@/selectors/user.selectors'
import {
  selectExpenseRatioGoalDifference,
  selectIncomeGoalPercentage,
  selectProfitGoal,
  selectProfitGoalPercentage,
  selectYtdExpenseRatio,
  selectYtdExpenses,
  selectYtdIncome,
  selectYtdProfit,
} from '@/features/goal-setting/goals.selectors'
import { TextColors, TypographyStyles } from '@/styles'

export const YearToDateProfitLossCard = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const fp = useReselector(getFinancialProfile)
  const [trendData, setTrendData] = useState<
    { [key: string]: string | number | undefined }[]
  >([])
  const track = useAnalyticsTrack()
  const todayDate = useMemo(() => DateTime.now(), [])

  const profitGoalPercentage = useReselector(selectProfitGoalPercentage)
  const ytdIncome = useReselector(selectYtdIncome)
  const ytdExpenses = useReselector(selectYtdExpenses)
  const profitGoal = useReselector(selectProfitGoal)
  const incomeGoalPercentage = useReselector(selectIncomeGoalPercentage)
  const ytdExpenseRatio = useReselector(selectYtdExpenseRatio)
  const expenseRatioDiff = useReselector(selectExpenseRatioGoalDifference)
  const ytdProfit = useReselector(selectYtdProfit)

  useEffect(() => {
    const fetchTrendData = async () => {
      const startDate = todayDate
        .startOf('year')
        .toFormat(DATE_FORMATS_LUXON.TIMESTAMP)
      const endDate = todayDate.toFormat(DATE_FORMATS_LUXON.TIMESTAMP)
      const endOfYear = todayDate
        .endOf('year')
        .toFormat(DATE_FORMATS_LUXON.TIMESTAMP)

      const fetchedData = await fetchInsightsBarchartData(
        INSIGHTS_TYPE.PROFIT,
        startDate,
        endDate
      )(dispatch)

      if (fetchedData) {
        const chartData = processDataForChart(
          fetchedData,
          [startDate, endOfYear],
          INSIGHTS_TYPE.PROFIT
        )
        setTrendData(chartData)
      }
    }
    fetchTrendData()
  }, [dispatch, todayDate])

  return (
    <Card>
      <div className="flex">
        <div className="w-5/8">
          <Text variant={TypographyStyles.H1}>Year to Date Profit & Loss</Text>
          <Text variant={TypographyStyles.BODY_SM}>
            {todayDate
              .startOf('year')
              .toFormat(DATE_FORMATS_LUXON.DISPLAY_LONG_SHORT_DAY)}{' '}
            - {todayDate.toFormat(DATE_FORMATS_LUXON.DISPLAY_LONG_SHORT_DAY)}
          </Text>
        </div>
        <div className="w-3/8 flex justify-end items-end">
          <Button
            variant="link"
            onClick={() => {
              navigate('/goal-setting')
            }}
            iconLeft={light('pencil')}
          >
            {fp?.annualIncomeGoalInCents || fp?.annualExpenseRatioGoal
              ? 'Edit Goals'
              : 'Set Goals'}
          </Button>
          <Button
            variant="link"
            onClick={() => {
              track('Clicked year to date profiles View Reports Button')
              navigate('/insights/overview')
            }}
            iconRight={light('arrow-right')}
          >
            View Reports
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-[1fr_1fr_1fr] gap-1">
        <Card color={CardColors.LEVEL_1}>
          <Text variant={TypographyStyles.DISPLAY_MD}>
            {formatCurrency(ytdIncome)}
          </Text>
          <Text variant={TypographyStyles.BODY_SM}>
            Year-to-Date Income
            <Popup
              content={
                <>
                  <Text variant={TypographyStyles.LABEL_MD}>
                    Year-to-Date Income
                  </Text>
                  <br />
                  <Text variant={TypographyStyles.BODY_SM}>
                    This is the total income that has been categorized by
                    Heard&apos;s bookkeeping team this calendar year.
                    <br />
                    <br />
                    If you’ve set a goal, the bar graph will track your progress
                    towards your income target that you set.
                    <br />
                    <br />
                    Use the “Edit Targets” button in the upper-right hand corner
                    of Year to Date Profit and Loss to update your target.
                  </Text>
                </>
              }
            />
          </Text>
          <br />
          {fp?.annualIncomeGoalInCents ? (
            <div>
              <div className="flex gap-2 items-center mb-3">
                <Progress value={incomeGoalPercentage} />
                <Text
                  color={TextColors.HIGHLIGHT}
                  variant={TypographyStyles.LABEL_SM}
                >
                  {incomeGoalPercentage}%
                </Text>
              </div>
              <Text>
                Annual Income Goal of{' '}
                {formatCurrencyFromCents(fp.annualIncomeGoalInCents, {
                  hideDecimalsIfZero: true,
                })}
              </Text>
            </div>
          ) : (
            <Button
              variant="secondary"
              onClick={() => navigate('/goal-setting')}
              className="w-full"
            >
              <Icon icon={regular('bullseye')} />
              Set Goal
            </Button>
          )}
          <br />
          <Link
            href="/insights/income"
            trackMessage="Clicked year to date income"
            iconRight={light('arrow-right')}
            className="mt-4"
          >
            View income breakdown
          </Link>
        </Card>
        <Card color={CardColors.LEVEL_1}>
          <Text variant={TypographyStyles.DISPLAY_MD}>
            -{formatCurrency(Math.abs(ytdExpenses))}
          </Text>
          <Text variant={TypographyStyles.BODY_SM}>
            Year-to-Date Expenses
            <Popup
              content={
                <>
                  <Text variant={TypographyStyles.LABEL_MD}>
                    Year-to-Date Expenses
                  </Text>
                  <Text variant={TypographyStyles.BODY_SM} className="mt-3">
                    This is the total amount of expenses that has been
                    categorized by Heard&apos;s bookkeeping team this calendar
                    year.
                    <br />
                    If you’ve set a goal, you will see your expense ratio (your
                    expenses as a percentage of your income) for the year
                    compared with your target.
                    <br />
                    Use the “Edit Targets” button in the upper-right hand corner
                    of Year to Date Profit and Loss to update your target.
                  </Text>
                </>
              }
            />
          </Text>
          <br />
          {fp?.annualExpenseRatioGoal ? (
            <div>
              <div className="flex gap-2 mb-2">
                <Text variant={TypographyStyles.DISPLAY_SM}>
                  {ytdExpenseRatio}%
                </Text>
                <Badge
                  color={expenseRatioDiff > 10 ? 'orange' : 'gray'}
                  variant="secondary"
                >
                  {expenseRatioDiff > 0 && '+'}
                  {expenseRatioDiff.toFixed(2)}%
                </Badge>
              </div>
              <Text variant={TypographyStyles.BODY_XS}>
                Expense Ratio Goal of {fp.annualExpenseRatioGoal * 100}%
              </Text>
            </div>
          ) : (
            <Button
              variant="secondary"
              onClick={() => navigate('/goal-setting')}
              className="w-full"
            >
              <Icon icon={regular('bullseye')} />
              Set Goal
            </Button>
          )}
          <br />
          <Link
            href="/insights/expenses"
            trackMessage="Clicked year to date expenses"
            iconRight={light('arrow-right')}
            className="mt-4"
          >
            View expenses breakdown
          </Link>
        </Card>

        <Card color={CardColors.LEVEL_1}>
          <Text variant={TypographyStyles.DISPLAY_MD}>
            {formatCurrency(ytdProfit, { hideDecimalsIfZero: true })}
          </Text>
          <Text variant={TypographyStyles.BODY_SM}>
            Year-to-Date Profit
            <Popup
              content={
                <>
                  <Text variant={TypographyStyles.LABEL_MD}>
                    Year-to-Date Profit
                  </Text>
                  <br />
                  <Text variant={TypographyStyles.BODY_SM}>
                    This is the total bookkeeper reconciled amount after
                    expenses are paid, or your take-home earnings before taxes
                    for this calendar year.
                    <br />
                    <br />
                    If you’ve set a goal, you will see your profit for the year
                    compared with your target.
                    <br />
                    <br />
                    Use the “Edit Targets” button in the upper-right hand corner
                    of Year to Date Profit and Loss to update your target.
                  </Text>
                </>
              }
            />
          </Text>
          <br />
          {fp?.annualIncomeGoalInCents && fp.annualExpenseRatioGoal ? (
            <div>
              <div className="flex gap-2 items-center mb-3">
                <Progress value={profitGoalPercentage} />
                <Text
                  color={TextColors.HIGHLIGHT}
                  variant={TypographyStyles.LABEL_SM}
                >
                  {profitGoalPercentage}%
                </Text>
              </div>
              <Text>
                Annual Profit Goal of{' '}
                {formatCurrency(profitGoal, { hideDecimalsIfZero: true })}
              </Text>
            </div>
          ) : (
            <Button
              variant="secondary"
              onClick={() => navigate('/goal-setting')}
              className="w-full"
            >
              <Icon icon={regular('bullseye')} />
              Set Goal
            </Button>
          )}
          <br />
          <Link
            href="/insights/profit"
            trackMessage="Clicked year to date profit"
            iconRight={light('arrow-right')}
            className="mt-4"
          >
            View profit breakdown
          </Link>
        </Card>
      </div>
      <Accordion type="single" collapsible>
        <Accordion.Item value="chart">
          <Accordion.Trigger>
            Year-to-Date Profit and Loss Chart
          </Accordion.Trigger>
          <Accordion.Content>
            <InsightsTrendGraph data={trendData} />
          </Accordion.Content>
        </Accordion.Item>
      </Accordion>
    </Card>
  )
}
